
import React, { useState, useEffect } from 'react';
import { getResponsiveImageProps } from '@/utils/imageOptimizer';

interface OptimizedImageProps {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  className?: string;
  loading?: 'lazy' | 'eager';
  quality?: number;
  sizes?: string;
  fallbackSrc?: string;
  placeholder?: string;
}

const OptimizedImage: React.FC<OptimizedImageProps> = ({
  src,
  alt,
  width,
  height,
  className = '',
  loading = 'lazy',
  quality = 80,
  sizes,
  fallbackSrc,
  placeholder
}) => {
  const [imgSrc, setImgSrc] = useState(src);
  const [hasError, setHasError] = useState(false);
  const responsiveProps = getResponsiveImageProps(src);

  useEffect(() => {
    setImgSrc(src);
    setHasError(false);
  }, [src]);

  const handleError = () => {
    if (fallbackSrc && !hasError) {
      setImgSrc(fallbackSrc);
      setHasError(true);
    }
  };

  // For SVG images, use simple img tag
  if (src.endsWith('.svg')) {
    return (
      <img
        src={imgSrc}
        alt={alt}
        width={width}
        height={height}
        loading={loading}
        decoding="async"
        className={className}
        onError={handleError}
      />
    );
  }

  // For other images, use responsive attributes
  return (
    <img
      src={imgSrc}
      alt={alt}
      width={width}
      height={height}
      loading={loading}
      decoding="async"
      className={className}
      sizes={sizes || responsiveProps.sizes}
      srcSet={responsiveProps.srcSet}
      onError={handleError}
    />
  );
};

export default OptimizedImage;
